import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { AppDialog } from "../../components/AppDialog";
import { AppDialogHeader } from "../../components/AppDialogHeader";
import { subscribeDialog, getCurrentDialog, resolveConfirm, resolvePrompt } from "../../../dialogBus";

// React port of dialogs.ts's confirm/prompt modals (window.confirm/prompt
// replacements). Renders whichever request is current from dialogBus -
// see dialogBus.ts for why only one can be open at a time.
export function DialogHost() {
  const request = useSyncExternalStore(subscribeDialog, getCurrentDialog);
  const [promptValue, setPromptValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (request?.kind !== "prompt") return;
    setPromptValue(request.initialValue);
    const t = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 200);
    return () => clearTimeout(t);
  }, [request]);

  if (!request) return null;

  if (request.kind === "confirm") {
    return (
      <AppDialog open onClose={() => resolveConfirm(false)} maxWidth="xs" fullWidth>
        <AppDialogHeader title={request.title} onBack={() => resolveConfirm(false)} />
        <DialogContent sx={{ p: 4, pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {request.message}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button variant="outlined" color="primary" onClick={() => resolveConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color={request.danger ? "error" : "primary"}
              onClick={() => resolveConfirm(true)}
            >
              {request.confirmLabel}
            </Button>
          </Box>
        </DialogContent>
      </AppDialog>
    );
  }

  return (
    <AppDialog open onClose={() => resolvePrompt(null)} maxWidth="xs" fullWidth>
      <AppDialogHeader title={request.title} onBack={() => resolvePrompt(null)} />
      <DialogContent sx={{ p: 4, pt: 2 }}>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            resolvePrompt(promptValue.trim() || null);
          }}
        >
          {request.message && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {request.message}
            </Typography>
          )}
          <TextField
            inputRef={inputRef}
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            placeholder={request.placeholder}
            fullWidth
            size="small"
            slotProps={{ htmlInput: { maxLength: 100 } }}
            sx={{ mb: 3 }}
          />
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button variant="outlined" color="primary" onClick={() => resolvePrompt(null)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {request.confirmLabel}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </AppDialog>
  );
}
